import { Outlet, Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Sidebar } from './Sidebar';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import * as Sentry from '@sentry/react';
import { ehErroDeChunkVelho, recarregarUmaVezPorChunkVelho } from '../../lib/chunkVelho';

function PageErrorFallback({ erro, resetError }: { erro: unknown; resetError: () => void }) {
  /*
    Aba que estava aberta quando saiu um deploy pede um chunk que nao existe
    mais, e a navegacao cai aqui. Recarregar resolve, e o usuario nao tem por
    que descobrir isso sozinho.

    A guarda contra laco fica em recarregarUmaVezPorChunkVelho: se ja
    recarregou ha pouco, ela devolve false e a tela de erro aparece normal, em
    vez de prender a pessoa num ciclo de recargas.
  */
  useEffect(() => {
    if (ehErroDeChunkVelho(erro)) recarregarUmaVezPorChunkVelho();
  }, [erro]);

  return (
    <div role="alert" className="ds-card text-center" style={{ padding: '2.5rem 2rem' }}>
      <h2 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: 18, color: 'var(--text)', marginBottom: '0.5rem' }}>
        Esta página falhou
      </h2>
      <p className="text-sm mb-4" style={{ color: 'var(--text-mid)' }}>
        Tente novamente. Se persistir, recarregue a página ou volte ao início.
      </p>
      <div className="flex items-center justify-center gap-2">
        <button onClick={resetError} className="ds-btn ds-btn-primary" style={{ padding: '9px 18px' }}>
          Tentar novamente
        </button>
        <button onClick={() => window.location.reload()} className="ds-btn ds-btn-secondary" style={{ padding: '9px 18px' }}>
          Recarregar
        </button>
      </div>
    </div>
  );
}

export function AppLayout() {
  const { user, loading } = useAuth();
  const { slug } = useParams();
  const location = useLocation();
  const [clientName, setClientName] = useState<string>();
  const [papelNoCliente, setPapelNoCliente] = useState<string | undefined>(undefined);

  useEffect(() => {
    // Não dispara fetch enquanto o auth ainda está booting — evita race
    // onde getClients corre em paralelo com tryRefresh, ambos chamando
    // refresh-singleton em desordem e resultando em 401 espúrio.
    if (loading || !user) return;

    if (!slug) {
      // Reset intencional ao trocar de slug — não há value anterior pra
      // condicionar (initial state também é undefined).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setClientName(undefined);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPapelNoCliente(undefined);
      return;
    }

    let cancelled = false;
    api.getClients().then(({ clients }) => {
      if (cancelled) return;
      const c = clients.find((cl) => cl.slug === slug);
      if (c) {
        setClientName(c.name);
        setPapelNoCliente(c.user_role);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [slug, loading, user]);

  if (loading) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Carregando aplicação"
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--bg)' }}
      >
        <div
          aria-hidden="true"
          className="animate-spin rounded-full h-8 w-8"
          style={{
            borderTop: '2px solid var(--cyan)',
            borderBottom: '2px solid var(--cyan)',
            borderLeft: '2px solid transparent',
            borderRight: '2px solid transparent',
          }}
        />
        <span className="sr-only">Carregando...</span>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--bg)' }}>
      {/* Skip link — primeiro elemento focável, WCAG 2.4.1 */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
        style={{ background: 'var(--cyan)', color: 'var(--bg)', fontWeight: 600 }}
      >
        Pular para o conteúdo
      </a>
      <Sidebar clientName={clientName} papelNoCliente={papelNoCliente} />
      <main id="main-content" className="flex-1 overflow-auto" tabIndex={-1}>
        {/* pt-[72px]: a barra fixa do celular tem 56px. */}
        <div className="p-4 pt-[72px] md:p-8">
          {/*
            ErrorBoundary scoped por rota — antes só havia um boundary no
            topo do <App>, e qualquer erro de página derrubava sidebar,
            sessão e layout inteiro. Aqui captura erro só do conteúdo da
            página atual; sidebar e contexto continuam renderizados.
            key=pathname reseta o boundary ao navegar (sem isso, mudar de
            rota não dispensa o erro).
          */}
          <Sentry.ErrorBoundary
            key={location.pathname}
            fallback={(errorData) => (
              // Sem desestruturar: resetError é método do objeto do Sentry e
              // separá-lo dispara unbound-method no lint.
              <PageErrorFallback erro={errorData.error} resetError={() => errorData.resetError()} />
            )}
          >
            <Outlet />
          </Sentry.ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
